import {
  CHAT_MESSAGE_MAX,
  takesAnswers,
  type ChatItem,
  type ConversationLanguage,
  type QuestionDefinition,
} from '@cyberscore/contracts';
import { ChevronRight, ClipboardList, FileSearch } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import { href } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { STATUS_LABEL } from '../assessments/AssessmentsPage';
import { useAssessment, useJourney, useQuestions } from '../assessments/api';
import { Progress } from '../assessments/QuestionFlow';
import { useSuggestions, useUpload } from '../documents/api';
import { uploadError } from '../documents/DocumentParts';
import { reviewGroups } from '../documents/files';
import { useChatAction, useConversation, type ChatAction } from './api';
import { ChatThread, Composer, type ChatHandlers } from './ChatThread';
import { THREAD_COPY, type ThreadCopy } from './copy';

const LANGUAGES: { key: ConversationLanguage; label: string; short: string }[] = [
  { key: 'en', label: 'English', short: 'EN' },
  { key: 'ms', label: 'Bahasa Malaysia', short: 'BM' },
];

/** What the screen says while the core works on each action. */
export const BUSY: Record<ChatAction['type'], keyof ThreadCopy['busy']> = {
  send: 'send',
  answer: 'save',
  confirm: 'save',
  decline: 'wait',
  skip: 'wait',
  language: 'language',
};

/** A message being sent, shown at once and replaced by the stored one when the core answers. */
export function sendingItem(text: string): ChatItem {
  return { kind: 'user', id: 'sending', text, via: 'typed' };
}

/**
 * The assessment as a conversation (task 28): the chat on the left, the journey's progress beside it. Taps
 * and form cards save at once; typed words come back on a confirmation card (docs/design/chat-ux.md).
 */
export function ChatScreen({ id }: { id: string }) {
  const detail = useAssessment(id);
  const qs = useQuestions(id);
  const journey = useJourney(id, !!detail.data?.journey_key);
  const chat = useConversation(id);
  const act = useChatAction(id);
  const upload = useUpload(id);
  const suggestions = useSuggestions(id);
  const toast = useToast();
  const [sending, setSending] = useState<string | null>(null);
  // A message that couldn't be sent goes back in the composer (remounted with it).
  const [draft, setDraft] = useState({ text: '', n: 0 });

  const questions = useMemo(
    () =>
      Object.fromEntries((qs.data?.questions ?? []).map((r) => [r.key, r.definition])) as Record<
        string,
        QuestionDefinition
      >,
    [qs.data],
  );

  if (detail.isPending || qs.isPending || chat.isPending) return <div className="empty">Loading…</div>;
  if (detail.isError || qs.isError || chat.isError) {
    const error = detail.error ?? qs.error ?? chat.error;
    return (
      <div className="empty">
        <strong>Could not open the conversation</strong>
        {error instanceof ApiError && error.status === 403
          ? 'Only people answering this assessment can use the chat. The assessment page shows its answers.'
          : error?.message}
        <a className="btn mt" href={href.assessment(id)}>
          Back to the assessment
        </a>
      </div>
    );
  }

  const view = chat.data;
  const language = view.conversation.language;
  const closed = !takesAnswers(detail.data.status);
  const t = THREAD_COPY[language];
  // While switching, say so in the language being switched to.
  const busy = upload.isPending
    ? t.busy.upload
    : act.isPending
      ? act.variables.type === 'language'
        ? THREAD_COPY[act.variables.language].busy.language
        : t.busy[BUSY[act.variables.type]]
      : null;
  const review = reviewGroups(suggestions.data ?? []);

  const run = (a: ChatAction) => {
    if (act.isPending) return;
    if (a.type === 'send') setSending(a.text);
    act.mutate(a, {
      onError: (e) => {
        if (a.type === 'send') setDraft((d) => ({ text: a.text, n: d.n + 1 }));
        toast(
          e instanceof ApiError && e.status === 409
            ? 'The conversation moved on, so here is where it stands now.'
            : `${a.type === 'send' ? 'Couldn’t send that' : 'Couldn’t save that'}: ${e.message}`,
          'bad',
        );
      },
      onSettled: () => setSending(null),
    });
  };
  const handlers: ChatHandlers = {
    answer: (question, value, _text, via) => run({ type: 'answer', question, value, via }),
    confirm: (card, qs) => run({ type: 'confirm', card, questions: qs }),
    decline: (card, qs) => run({ type: 'decline', card, questions: qs }),
    skip: (question) => run({ type: 'skip', question }),
    // The file is stored and scanned first; the answer is then saved like a form card.
    upload: (question, file) => {
      if (busy) return;
      upload.mutate(
        { file, question },
        {
          onSuccess: (r) =>
            run({ type: 'answer', question, value: { document_ids: [r.document.id] }, via: 'form' }),
          onError: (e) => toast(uploadError(e), 'bad'),
        },
      );
    },
  };
  const items = sending ? [...view.items, sendingItem(sending)] : view.items;
  const rows = qs.data.questions;

  return (
    <>
      <div className="crumbs">
        <a href={href.assessments()}>Assessments</a>
        <ChevronRight size={14} />
        <a href={href.assessment(id)}>{detail.data.customer_name}</a>
        <ChevronRight size={14} />
        Chat
      </div>
      <div className="chat-layout">
        <section className="card chat" aria-label="Assessment chat">
          <div className="chat-h">
            <div className="grow">
              <b>Cyber check</b>
              <span>{detail.data.customer_name}</span>
            </div>
            <div className="seg" role="group" aria-label="Chat language">
              {LANGUAGES.map((l) => (
                <button
                  key={l.key}
                  type="button"
                  title={l.label}
                  aria-label={l.label}
                  aria-pressed={language === l.key}
                  disabled={!!busy}
                  onClick={() => language !== l.key && run({ type: 'language', language: l.key })}
                >
                  {l.short}
                </button>
              ))}
            </div>
            <a className="btn btn-sm btn-ghost" href={href.answer(id, view.live ?? undefined)} title="Answer in the form instead" aria-label="Answer in the form">
              <ClipboardList size={14} /> <span className="hide-sm">Form</span>
            </a>
          </div>
          <ChatThread items={items} questions={questions} handlers={handlers} busy={busy} readOnly={closed} language={language} />
          {closed ? (
            <p className="chat-closed">
              This assessment is {STATUS_LABEL[detail.data.status].toLowerCase()}, so answers can no longer change.
            </p>
          ) : (
            <>
              {review.total > 0 && (
                <a className="chat-review" href={href.documents(id)}>
                  <FileSearch size={14} /> {t.reviewDocs(review.total, review.conflicts.length)}
                </a>
              )}
              {view.live && (
                <div className="chat-try" aria-label="Ask about this question">
                  {t.ask.map((s) => (
                    <button key={s} type="button" className="chip" disabled={!!busy} onClick={() => run({ type: 'send', text: s })}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <Composer
                key={draft.n}
                initial={draft.text}
                maxLength={CHAT_MESSAGE_MAX}
                disabled={!!busy}
                language={language}
                onSend={(text) => run({ type: 'send', text })}
              />
            </>
          )}
        </section>
        <Progress
          rows={rows}
          journey={journey.data ?? null}
          current={rows.find((r) => r.key === view.live)}
          feedback={null}
        />
      </div>
    </>
  ); // prettier-ignore
}
